// Uređaj za "Kuhinjski ekran": veličina prozora, veza, zvuk, cijeli ekran, ne gasi ekran, otkucaj od sekunde.
// Svi slušaoci i tajmeri se čiste u beforeDestroy.
//
// Očekuje u komponenti: settings, tickets, offline, dateMode, $refs.board (pageAll), onKey(), onResize hook nije potreban.
import { TH, needsReminder } from "@/utils/kdsTickets";

const QUANTUM_MS = 5000; // kartice se crtaju svakih 5 s (sat prikazuje minute), a ne svake sekunde

export default {
  data() {
    return {
      viewW: typeof window === "undefined" ? 1366 : window.innerWidth,
      viewNow: Math.floor(Date.now() / QUANTUM_MS) * QUANTUM_MS,
      browserOnline: typeof navigator === "undefined" ? true : navigator.onLine !== false,
      connAge: 0,
      audioArmed: false,
      fullscreenOn: false,
    };
  },
  computed: {
    // širina ploče: xl TV, lg laptop, md uži laptop/tablet, nr = jedna kolona i tabovi ispod
    wc() {
      const w = this.viewW;
      return w >= 1600 ? "xl" : w >= 1200 ? "lg" : w >= 1000 ? "md" : "nr";
    },
    phone() {
      return this.viewW < 600;
    },
    // jedinica za tekst: TV 20 px, laptop i tablet 17 px, telefon 16 px (4K bez skaliranja raste srazmjerno)
    unit() {
      const w = this.viewW;
      if (w >= 2400) return Math.round((20 * w) / 1920);
      return w >= 1600 ? 20 : w >= 600 ? 17 : 16;
    },
    soundBlocked() {
      return this.settings.sound && !this.audioArmed;
    },
    fullscreenSupported() {
      return typeof document !== "undefined" && !!document.fullscreenEnabled;
    },
    wakeSupported() {
      return typeof navigator !== "undefined" && "wakeLock" in navigator;
    },
  },
  created() {
    this._ctx = null;
    this._wake = null;
    this._handlers = [];
    this._tickTimer = null;
    this._lastRemind = 0;
    this._lastPage = Date.now();
  },
  mounted() {
    const on = (target, type, fn, opts) => {
      target.addEventListener(type, fn, opts);
      this._handlers.push({ target, type, fn, opts });
    };
    on(window, "resize", this.onViewport);
    on(window, "online", this.onOnline);
    on(window, "offline", this.onOffline);
    on(document, "visibilitychange", this.onVisibility);
    on(document, "fullscreenchange", this.onFullscreen);
    // svaki dodir ili taster otključava zvuk (pravilo preglednika)
    on(document, "pointerdown", this.armAudio, true);
    on(document, "keydown", this.armAudio, true);
    on(document, "keydown", this.onKey);
    this._tickTimer = setInterval(this.onTick, 1000);
    this.applyWake();
  },
  beforeDestroy() {
    this._handlers.forEach((h) => h.target.removeEventListener(h.type, h.fn, h.opts));
    this._handlers = [];
    clearInterval(this._tickTimer);
    this.releaseWake();
    if (this._ctx && this._ctx.close) {
      try {
        this._ctx.close();
      } catch (e) {
        // bez zvuka
      }
    }
  },
  methods: {
    onViewport() {
      const w = window.innerWidth;
      if (w >= 200) this.viewW = w; // privremeni prozor od 1 px (npr. pri snimanju ekrana) ne smije da preslaže ploču
    },
    onOnline() {
      this.browserOnline = true;
      if (this.loadTickets) this.loadTickets();
    },
    onOffline() {
      this.browserOnline = false;
    },

    // ---------- otkucaj ----------
    onTick() {
      const now = Date.now();
      const quantum = Math.floor(now / QUANTUM_MS) * QUANTUM_MS;
      if (quantum !== this.viewNow) this.viewNow = quantum;
      if (this.offline) this.connAge = Math.floor((now - (this.lastOk || this.startedAt)) / 1000);
      this.tickReminder(now);
      this.tickPaging(now);
    },
    // tih podsjetnik dok neprihvaćena narudžba čeka duže od 2 min (samo uživo, samo uz uključen zvuk)
    tickReminder(now) {
      const s = this.settings;
      if (!s.sound || !s.remind || this.offline || this.dateMode !== "live") return;
      if (now - this._lastRemind < TH.remindEvery * 1000) return;
      if (needsReminder(this.tickets, now)) {
        this._lastRemind = now;
        this.chime(true);
      }
    },
    // automatsko listanje (nije podrazumijevano): svakih 9 s pomjeri kolone koje ne staju
    tickPaging(now) {
      if (this.settings.paging !== "auto" || now - this._lastPage < TH.pageMs) return;
      this._lastPage = now;
      if (this.$refs.board && this.$refs.board.pageAll) this.$refs.board.pageAll();
    },

    // ---------- zvuk ----------
    // Zvuk ne smije da krene prije prvog dodira; do tada se vidi traka "Zvuk je blokiran".
    armAudio() {
      if (this.audioArmed) return;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) {
        this.audioArmed = true; // preglednik nema zvuk: nema ni trake "Zvuk je blokiran"
        return;
      }
      try {
        const wasBlocked = this.settings.sound; // prvi dodir uz uključen zvuk: tih ton potvrđuje da zvuk radi
        this._ctx = this._ctx || new Ctx();
        const result = this._ctx.resume();
        this.audioArmed = true;
        if (result && result.then) {
          result.then(
            () => {
              if (wasBlocked) this.chime(true);
            },
            () => {
              this.audioArmed = false;
            }
          );
        } else if (wasBlocked) {
          this.chime(true);
        }
      } catch (e) {
        // bez zvuka, ekran radi dalje
      }
    },
    unlockSound() {
      this.armAudio();
      this.chime(true);
    },
    // dva kratka tona; soft = tih podsjetnik
    chime(soft) {
      if (!this.settings.sound) return;
      const ctx = this._ctx;
      if (!ctx || !this.audioArmed) return;
      try {
        if (ctx.state === "suspended") {
          const result = ctx.resume();
          if (result && result.catch) result.catch(() => {});
        }
        const t0 = ctx.currentTime;
        [
          [880, 0],
          [1175, 0.17],
        ].forEach(([freq, delay]) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sine";
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(0.0001, t0 + delay);
          gain.gain.exponentialRampToValueAtTime(soft ? 0.05 : 0.18, t0 + delay + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, t0 + delay + 0.3);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(t0 + delay);
          osc.stop(t0 + delay + 0.32);
        });
      } catch (e) {
        // bez zvuka
      }
    },

    // ---------- cijeli ekran, ne gasi ekran ----------
    toggleFullscreen() {
      try {
        if (document.fullscreenElement) document.exitFullscreen();
        else document.documentElement.requestFullscreen();
      } catch (e) {
        // preglednik ne dozvoljava
      }
    },
    onFullscreen() {
      this.fullscreenOn = !!document.fullscreenElement;
    },
    async applyWake() {
      if (!this.wakeSupported || !this.settings.wake || this._wake || document.visibilityState !== "visible") return;
      try {
        this._wake = await navigator.wakeLock.request("screen");
        this._wake.addEventListener("release", () => {
          this._wake = null;
        });
      } catch (e) {
        this._wake = null;
      }
    },
    releaseWake() {
      if (this._wake) {
        this._wake.release().catch(() => {});
        this._wake = null;
      }
    },
    onVisibility() {
      if (document.visibilityState === "visible") this.applyWake();
    },
  },
  watch: {
    "settings.wake"(on) {
      if (on) this.applyWake();
      else this.releaseWake();
    },
    "settings.sound"(on) {
      // uključivanje zvuka je dodir, pa se odmah čuje probni ton
      if (on) {
        this.armAudio();
        this.chime(true);
      }
    },
  },
};
